import { HttpClient } from '@angular/common/http';
import { computed, inject, Injectable, NgZone, signal } from '@angular/core';
import { FirebaseApp, initializeApp } from 'firebase/app';
import {
  getMessaging,
  getToken,
  MessagePayload,
  Messaging,
  onMessage
} from 'firebase/messaging';
import { Observable, of } from 'rxjs';
import { AuthStorageService } from '../auth/core/auth-storage.service';
import { WEB_PUSH_CONFIG } from './web-push.config';

export enum NotificationPermissionState {
  Default = 'default',
  Granted = 'granted',
  Denied = 'denied'
}

export type UnsupportedReason =
  | 'in-app-browser'
  | 'ios-non-safari'
  | 'ios-old'
  | 'private-mode'
  | 'old-browser'
  | null;

export interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
  prompt(): Promise<void>;
}

@Injectable({ providedIn: 'root' })
export class WebPushService {
  private http = inject(HttpClient);
  private ngZone = inject(NgZone);
  private config = inject(WEB_PUSH_CONFIG, { optional: true });
  private authStorage = inject(AuthStorageService);

  private readonly pushNotificationsUrl = '/api/PushNotifications';

  // ── Firebase instances ──
  private firebaseApp: FirebaseApp | null = null;
  private messaging: Messaging | null = null;

  // ── Platform Detection (private writable, public readonly) ──
  private readonly _permissionState = signal<NotificationPermission>(
    NotificationPermissionState.Default
  );
  private readonly _fcmToken = signal<string | null>(null);
  private readonly _isSupported = signal<boolean>(false);
  private readonly _isIos = signal<boolean>(false);
  private readonly _isAndroid = signal<boolean>(false);
  private readonly _isStandalone = signal<boolean>(false);
  private readonly _hasInstallPrompt = signal<boolean>(false);
  private readonly _isInAppBrowser = signal<boolean>(false);
  private readonly _isIosNonSafari = signal<boolean>(false);
  private readonly _isPrivateMode = signal<boolean>(false);
  private readonly _iosVersion = signal<number | null>(null);
  private readonly _unsupportedReason = signal<UnsupportedReason>(null);

  // ── Public read-only signals ──
  readonly permissionState = this._permissionState.asReadonly();
  readonly fcmToken = this._fcmToken.asReadonly();
  readonly isSupported = this._isSupported.asReadonly();
  readonly isIos = this._isIos.asReadonly();
  readonly isAndroid = this._isAndroid.asReadonly();
  readonly isStandalone = this._isStandalone.asReadonly();
  readonly hasInstallPrompt = this._hasInstallPrompt.asReadonly();
  readonly isInAppBrowser = this._isInAppBrowser.asReadonly();
  readonly isIosNonSafari = this._isIosNonSafari.asReadonly();
  readonly isPrivateMode = this._isPrivateMode.asReadonly();
  readonly iosVersion = this._iosVersion.asReadonly();
  readonly unsupportedReason = this._unsupportedReason.asReadonly();

  // ── Computed ──
  readonly needsHomeScreenInstall = computed(
    () =>
      this.isIos() &&
      !this.isStandalone() &&
      !this.isIosNonSafari() &&
      (this.iosVersion() ?? 0) >= 16.4
  );

  readonly canRequestPermission = computed(
    () =>
      this.isSupported() &&
      !this.needsHomeScreenInstall() &&
      !this.unsupportedReason()
  );

  readonly showUnsupportedMessage = computed(
    () => this.unsupportedReason() !== null
  );

  // ── Private state ──
  private deferredInstallPrompt: BeforeInstallPromptEvent | null = null;
  private initialized = false;

  // ── Initialization ──

  async initialize(): Promise<void> {
    if (this.initialized) return;
    this.initialized = true;

    this.detectPlatform();
    await this.detectPrivateMode();
    this.determineUnsupportedReason();
    this.listenForInstallPrompt();

    // Update current permission state
    if ('Notification' in window) {
      this._permissionState.set(window.Notification.permission);
    }

    // Only init Firebase if environment is supported
    if (this._isSupported() && !this._unsupportedReason()) {
      this.initFirebase();
    }

    // If already granted, refresh the token
    if (
      this.permissionState() === NotificationPermissionState.Granted &&
      this.messaging
    ) {
      await this.refreshTokenIfNeeded();
    }
  }

  // ── Permission & Token ──

  async requestPermission(): Promise<boolean> {
    if (!this.canRequestPermission()) return false;

    try {
      const permission = await window.Notification.requestPermission();
      this.ngZone.run(() => this._permissionState.set(permission));

      if (permission === NotificationPermissionState.Granted) {
        await this.retrieveAndRegisterToken();
        return true;
      }

      return false;
    } catch (error) {
      console.error('[WebPushService] Permission request failed:', error);
      return false;
    }
  }

  async triggerInstallPrompt(): Promise<boolean> {
    if (!this.deferredInstallPrompt) return false;

    try {
      await this.deferredInstallPrompt.prompt();
      const choice = await this.deferredInstallPrompt.userChoice;
      this.deferredInstallPrompt = null;
      this.ngZone.run(() => this._hasInstallPrompt.set(false));

      if (choice.outcome === 'accepted') {
        localStorage.setItem('push_install_completed', 'true');
        return true;
      }
      return false;
    } catch (error) {
      console.error('[WebPushService] Install prompt failed:', error);
      return false;
    }
  }

  async refreshTokenIfNeeded(): Promise<void> {
    if (
      !this.messaging ||
      this.permissionState() !== NotificationPermissionState.Granted
    )
      return;

    try {
      await this.retrieveAndRegisterToken();
    } catch (error) {
      console.error('[WebPushService] Token refresh failed:', error);
    }
  }

  // ── Foreground Messages ──

  onForegroundMessage(callback: (payload: MessagePayload) => void): void {
    if (!this.messaging) return;

    onMessage(this.messaging, (payload: MessagePayload) => {
      console.log('[WebPushService] Foreground message:', payload);
      this.ngZone.run(() => callback(payload));
    });
  }

  // ── Token Registration with Backend ──

  registerToken(token: string): Observable<unknown> {
    const body = {
      fcmToken: token,
      deviceType: this.getDevicePlatform(),
      deviceId: this.getDeviceId(),
      deviceName: this.getDeviceName()
    };

    return this.http.post(`${this.pushNotificationsUrl}/register-device`, body);
  }

  unregisterToken(): Observable<unknown | null> {
    const token = this.fcmToken();
    if (!token) return of(null);

    const body = {
      fcmToken: token,
      deviceType: this.getDevicePlatform()
    };

    return this.http.post(
      `${this.pushNotificationsUrl}/unregister-device`,
      body
    );
  }

  // ── Cleanup (call on logout) ──

  /**
   * Clears local push-notification state only. Does NOT call the backend.
   *
   * Server-side device-token removal is the caller's responsibility and must
   * happen while the auth token is still valid — see `AuthService.logout`,
   * which calls `unregisterToken()` before tearing down the session. Doing the
   * network call here (fire-and-forget) risked leaving the device registered
   * on the server if it failed, so it would keep receiving another user's
   * notifications on shared devices.
   */
  cleanup(): void {
    this._fcmToken.set(null);
    this._permissionState.set(NotificationPermissionState.Default);
    this.initialized = false;
    this.firebaseApp = null;
    this.messaging = null;
  }

  // ── Prompt Dismissal ──

  /** How many days before the push prompt is shown again after dismissal. */
  private readonly PROMPT_DISMISS_DAYS = 30;

  dismissPrompt(): void {
    const userId = this.authStorage.state.user?.id;
    if (!userId) return;
    localStorage.setItem(
      `push_prompt_dismissed_${userId}`,
      new Date().toISOString()
    );
  }

  isPromptDismissed(): boolean {
    const userId = this.authStorage.state.user?.id;
    if (!userId) return false;
    const value = localStorage.getItem(`push_prompt_dismissed_${userId}`);
    if (!value) return false;

    const dismissedAt = new Date(value).getTime();
    if (isNaN(dismissedAt)) return false;

    const daysSince = (Date.now() - dismissedAt) / (1000 * 60 * 60 * 24);
    return daysSince < this.PROMPT_DISMISS_DAYS;
  }

  dismissUnsupportedBanner(): void {
    sessionStorage.setItem('push_unsupported_dismissed', 'true');
  }

  isUnsupportedBannerDismissed(): boolean {
    return sessionStorage.getItem('push_unsupported_dismissed') === 'true';
  }

  // ── Private: Firebase ──

  private initFirebase(): void {
    try {
      const firebaseConfig = this.config?.firebase;
      if (!firebaseConfig || firebaseConfig.apiKey === 'YOUR_API_KEY') {
        console.warn(
          '[WebPushService] Firebase config not set — skipping initialization'
        );
        return;
      }

      this.firebaseApp = initializeApp(firebaseConfig);
      this.messaging = getMessaging(this.firebaseApp);
    } catch (error) {
      console.error('[WebPushService] Firebase init failed:', error);
    }
  }

  private async retrieveAndRegisterToken(): Promise<void> {
    if (!this.messaging) return;

    const vapidKey = this.config?.vapidKey;
    if (!vapidKey || vapidKey === 'YOUR_VAPID_KEY') {
      console.warn(
        '[WebPushService] VAPID key not set — skipping token retrieval'
      );
      return;
    }

    // Wait for an *active* service worker rather than reading the current
    // registration, which can be undefined if token retrieval races the
    // async registration kicked off in main.ts. `ready` resolves only once a
    // registration is active, so getToken always receives a usable worker.
    const registration = await navigator.serviceWorker.ready;

    const token = await getToken(this.messaging, {
      vapidKey,
      serviceWorkerRegistration: registration
    });

    if (token) {
      this.ngZone.run(() => this._fcmToken.set(token));
      this.registerToken(token).subscribe({
        next: () =>
          console.log('[WebPushService] Token registered with backend'),
        error: (err: unknown) =>
          console.error('[WebPushService] Token registration failed:', err)
      });
    }
  }

  // ── Private: Platform Detection ──

  private detectPlatform(): void {
    const ua = navigator.userAgent;

    // iOS detection
    const isIos =
      /iPad|iPhone|iPod/.test(ua) ||
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    this._isIos.set(isIos);

    // Android detection
    this._isAndroid.set(/Android/i.test(ua));

    // Standalone mode
    const isStandalone =
      window.matchMedia('(display-mode: standalone)').matches ||
      (window.navigator as Navigator & { standalone?: boolean }).standalone ===
        true;
    this._isStandalone.set(isStandalone);

    // iOS version
    if (isIos) {
      const match = ua.match(/OS (\d+)_(\d+)/);
      if (match) {
        this._iosVersion.set(parseFloat(`${match[1]}.${match[2]}`));
      }
    }

    // In-app browser detection
    const isInApp = /FBAN|FBAV|Instagram|Line\/|Twitter|Snapchat|TikTok/i.test(
      ua
    );
    this._isInAppBrowser.set(isInApp);

    // iOS non-Safari detection
    if (isIos && !isInApp) {
      const isSafari =
        /Safari/.test(ua) && !/CriOS|FxiOS|OPiOS|EdgiOS/.test(ua);
      this._isIosNonSafari.set(!isSafari);
    }

    // Push API + SW support
    const pushSupported =
      'serviceWorker' in navigator &&
      'PushManager' in window &&
      'Notification' in window;
    this._isSupported.set(pushSupported);
  }

  /** Max time to wait for the IndexedDB private-mode probe before giving up. */
  private readonly PRIVATE_MODE_PROBE_TIMEOUT_MS = 1000;

  private async detectPrivateMode(): Promise<void> {
    try {
      // Heuristic: opening IndexedDB throws/errors in some legacy private modes.
      // Modern Safari/Chrome allow it, so only an explicit `onerror` is treated
      // as a private-mode signal. A timeout or `onblocked` resolves as
      // "not private" so initialization can never stall on this probe.
      await new Promise<void>((resolve, reject) => {
        const timer = setTimeout(resolve, this.PRIVATE_MODE_PROBE_TIMEOUT_MS);
        const settle = (cb: () => void) => {
          clearTimeout(timer);
          cb();
        };

        const request = indexedDB.open('push-test');
        request.onsuccess = () =>
          settle(() => {
            request.result.close();
            indexedDB.deleteDatabase('push-test');
            resolve();
          });
        request.onerror = () => settle(reject);
        request.onblocked = () => settle(resolve);
      });
      this._isPrivateMode.set(false);
    } catch {
      this._isPrivateMode.set(true);
    }
  }

  private determineUnsupportedReason(): void {
    if (this._isInAppBrowser()) {
      this._unsupportedReason.set('in-app-browser');
      return;
    }

    if (this._isIosNonSafari()) {
      this._unsupportedReason.set('ios-non-safari');
      return;
    }

    if (this._isIos() && !this._isStandalone()) {
      const version = this._iosVersion();
      if (version !== null && version < 16.4) {
        this._unsupportedReason.set('ios-old');
      }
      // iOS 16.4+ in a regular tab: PushManager is unavailable but this is expected —
      // needsHomeScreenInstall handles prompting the user to add to Home Screen.
      return;
    }

    if (this._isPrivateMode()) {
      this._unsupportedReason.set('private-mode');
      return;
    }

    if (!this._isSupported()) {
      this._unsupportedReason.set('old-browser');
      return;
    }

    this._unsupportedReason.set(null);
  }

  // ── Private: Install Prompt ──

  private listenForInstallPrompt(): void {
    window.addEventListener('beforeinstallprompt', (event: Event) => {
      event.preventDefault();
      this.deferredInstallPrompt = event as BeforeInstallPromptEvent;
      this.ngZone.run(() => this._hasInstallPrompt.set(true));
    });
  }

  // ── Private: Device Info ──

  private getDevicePlatform(): string {
    if (this._isIos()) return 'iOS';
    if (this._isAndroid()) return 'Android';
    return 'Web';
  }

  private getDeviceId(): string {
    let deviceId = localStorage.getItem('push_device_id');
    if (!deviceId) {
      deviceId = crypto.randomUUID();
      localStorage.setItem('push_device_id', deviceId);
    }
    return deviceId;
  }

  private getDeviceName(): string {
    const ua = navigator.userAgent;
    if (this._isIos()) return 'iOS Browser';
    if (this._isAndroid()) return 'Android Browser';
    if (/Mac/.test(ua)) return 'Mac Browser';
    if (/Windows/.test(ua)) return 'Windows Browser';
    if (/Linux/.test(ua)) return 'Linux Browser';
    return 'Web Browser';
  }
}
